import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { resetAuthCharIdFallback } from '@/hooks/useMessages';
import {
  cacheToken,
  clearCachedToken,
  devAuthBypass,
  installWebViewAuthBridge,
  api,
} from '@/lib/axios';
import type { AuthUser, PlayerData } from '@/vite-env.d';

const VALIDATE_MAX_ATTEMPTS = 3;
const VALIDATE_RETRY_MS = 450;
const PANEL_AUTH_POLL_MS = 10_000;
const REAUTH_DEBOUNCE_MS = 300;
const MIN_HIDDEN_MS = 400;

function authErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const res = (err as { response?: { data?: { message?: string }; status?: number } }).response;
    if (res?.data?.message) return res.data.message;
    if (res?.status === 400) {
      return 'Missing session token. The game client must send X-Session-Token to the API.';
    }
  }
  return 'Authentication failed';
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface ValidateSessionPayload {
  token: string;
  expiresIn: number;
  user: AuthUser;
  player: PlayerData | null;
}

async function requestValidateSession(): Promise<ValidateSessionPayload> {
  const { data } = await api.get<{
    status: string;
    data?: ValidateSessionPayload;
    message?: string;
  }>('/api/v1/auth/validate-session', { skipAuthRefresh: true });

  if (data.status === 'success' && data.data?.token) {
    return data.data;
  }

  throw new Error(data.message ?? 'Session validation failed');
}

async function validateSessionWithRetry(): Promise<ValidateSessionPayload> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= VALIDATE_MAX_ATTEMPTS; attempt++) {
    try {
      return await requestValidateSession();
    } catch (err) {
      lastError = err;
      if (attempt < VALIDATE_MAX_ATTEMPTS) {
        await sleep(VALIDATE_RETRY_MS * attempt);
      }
    }
  }

  throw lastError;
}

export interface AuthState {
  user: AuthUser | null;
  player: PlayerData | null;
  isLoading: boolean;
  error: string | null;
  revalidate: () => Promise<void>;
  lastAuthAt: number | null;
}

/** boot = apertura (spinner). poll = revalidación periódica sin spinner */
export type AuthInitMode = 'boot' | 'poll';

/**
 * Fail-closed: sin validate-session OK no hay panel — nunca conservar user/player viejos.
 */
export function useAuth(): AuthState {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [player, setPlayer] = useState<PlayerData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastAuthAt, setLastAuthAt] = useState<number | null>(null);
  const prevCharId = useRef<number | null>(null);
  const hiddenAt = useRef<number | null>(null);
  const reauthTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initAuthRef = useRef<(mode: AuthInitMode) => Promise<void>>(async () => {});

  const failClosed = useCallback(
    (err: unknown) => {
      clearCachedToken();
      setUser(null);
      setPlayer(null);
      setLastAuthAt(null);
      setError(authErrorMessage(err));
      if (typeof window !== 'undefined') {
        delete window.__APPWEB_LAST_AUTH_AT__;
        delete window.__APPWEB_JWT__;
      }
      queryClient.clear();
      resetAuthCharIdFallback();
    },
    [queryClient],
  );

  const applySession = useCallback(
    (session: ValidateSessionPayload) => {
      const prev = prevCharId.current;
      cacheToken(session.token, session.expiresIn);
      installWebViewAuthBridge();
      setUser(session.user);
      setPlayer(session.player);
      setLastAuthAt(Date.now());
      setError(null);
      prevCharId.current = session.user.charId;
      if (typeof window !== 'undefined') {
        window.__APPWEB_LAST_AUTH_AT__ = Date.now();
      }

      if (prev !== null && prev !== session.user.charId) {
        queryClient.invalidateQueries();
      }
    },
    [queryClient],
  );

  const initAuth = useCallback(
    async (mode: AuthInitMode = 'boot') => {
      if (mode === 'boot') {
        setIsLoading(true);
        clearCachedToken();
        setUser(null);
        setPlayer(null);
      }

      try {
        const session = await validateSessionWithRetry();
        applySession(session);
      } catch (err) {
        if (mode === 'boot' && devAuthBypass) {
          installWebViewAuthBridge();
          setUser({ jid: 999, charId: 999 });
          setPlayer({
            CharID: 999,
            CharName16: 'DevPlayer',
            RefObjID: 14875,
            CurLevel: 110,
            Strength: 100,
            Intellect: 100,
            JID: 999,
          });
          setLastAuthAt(Date.now());
          setError(null);
          prevCharId.current = 999;
        } else {
          failClosed(err);
        }
      } finally {
        if (mode === 'boot') {
          setIsLoading(false);
        }
      }
    },
    [applySession, failClosed],
  );

  initAuthRef.current = initAuth;

  const schedulePanelReauth = useCallback((mode: AuthInitMode) => {
    if (reauthTimer.current) clearTimeout(reauthTimer.current);
    reauthTimer.current = setTimeout(() => {
      void initAuthRef.current(mode);
    }, REAUTH_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    void initAuth('boot');
  }, [initAuth]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt.current = Date.now();
        return;
      }
      if (document.visibilityState !== 'visible') return;
      const hiddenMs = hiddenAt.current ? Date.now() - hiddenAt.current : MIN_HIDDEN_MS + 1;
      schedulePanelReauth(hiddenMs >= MIN_HIDDEN_MS ? 'boot' : 'poll');
    };

    const onFocus = () => {
      schedulePanelReauth('poll');
    };

    const onPageShow = (event: PageTransitionEvent) => {
      schedulePanelReauth(event.persisted ? 'boot' : 'poll');
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      if (reauthTimer.current) clearTimeout(reauthTimer.current);
    };
  }, [schedulePanelReauth]);

  useEffect(() => {
    let cancelled = false;

    const poll = () => {
      if (cancelled) return;
      void initAuthRef.current('poll');
    };

    const soon = window.setTimeout(poll, 800);
    const interval = window.setInterval(poll, PANEL_AUTH_POLL_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(soon);
      window.clearInterval(interval);
    };
  }, []);

  return {
    user,
    player,
    isLoading,
    error,
    lastAuthAt,
    revalidate: () => initAuth('boot'),
  };
}
